import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { mobile } from '../styles/MobileUtils';
import MobilePlanner from './MobilePlanner';
import MobileTaskEditor from './MobileTaskEditor';

interface ResponsivePlannerProps {
  plannerData: any;
  onTaskToggle: (date: string, taskType: 'chore' | 'lunchbox' | 'dinner') => void;
  onTimeSlotToggle: (task: any, date: string, taskIndex: number) => void; // New handler for timeSlots
  onTaskEdit: (task: any) => void;
  onTaskSave: (task: any) => void;
  onDateNavigate: (direction: 'prev' | 'next') => void;
  currentDate: Date;
  selectedMembers?: Set<string>; // Add member filter support
  householdMembers?: { name: string; age?: string }[]; // Add household members support
  profileData?: any; // Add profile data support
  children: React.ReactNode; // Desktop planner content
}

const ResponsiveContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const DesktopWrapper = styled.div`
  display: block;
  
  ${mobile(`
    display: none;
  `)}
`;

const MobileWrapper = styled.div`
  display: none;
  
  ${mobile(`
    display: block;
  `)}
`;

const useScreenSize = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      // Use theme breakpoint for consistency
      setIsMobile(window.innerWidth <= parseInt(theme.breakpoints.mobile));
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return isMobile;
};

const ResponsivePlanner: React.FC<ResponsivePlannerProps> = ({
  plannerData,
  onTaskToggle,
  onTimeSlotToggle,
  onTaskEdit,
  onTaskSave,
  onDateNavigate,
  currentDate,
  selectedMembers,
  householdMembers,
  profileData,
  children
}) => {
  const isMobile = useScreenSize();
  const [isTaskEditorOpen, setIsTaskEditorOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const handleTaskEdit = (task: any) => {
    setEditingTask(task);
    setIsTaskEditorOpen(true);
  };

  const handleAddTask = () => {
    setEditingTask(null); // No initial task for new task creation
    setIsTaskEditorOpen(true);
  };

  const handleTaskSave = (task: any) => {
    // Add current date if creating a new task
    const taskWithDate = {
      ...task,
      date: task.date || new Date().toISOString().split('T')[0]
    };
    onTaskSave(taskWithDate);
    setIsTaskEditorOpen(false);
    setEditingTask(null);
  };

  const handleTaskEditorClose = () => {
    setIsTaskEditorOpen(false);
    setEditingTask(null);
  };

  return (
    <ResponsiveContainer>
      <DesktopWrapper>
        {children}
      </DesktopWrapper>
      
      <MobileWrapper>
        <MobilePlanner
          plannerData={plannerData}
          onTaskToggle={onTaskToggle}
          onTimeSlotToggle={onTimeSlotToggle}
          onTaskEdit={handleTaskEdit}
          onAddTask={handleAddTask}
          onDateNavigate={onDateNavigate}
          currentDate={currentDate}
          selectedMembers={selectedMembers}
        />
        
        <MobileTaskEditor
          isOpen={isTaskEditorOpen}
          onClose={handleTaskEditorClose}
          onSave={handleTaskSave}
          initialTask={editingTask}
          householdMembers={householdMembers}
          profileData={profileData}
        />
      </MobileWrapper>
    </ResponsiveContainer>
  );
};

export default ResponsivePlanner; 